import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileApi, CreateProfileData } from '../api/profile';
import { useAuthStore } from '../store/authStore';
import './Profile.css';

export const Profile: React.FC = () => {
  const [profile, setProfile] = useState<CreateProfileData>({
    firstName: '',
    bio: '',
    department: '',
    year: 1,
    interests: [],
  });
  const [newInterest, setNewInterest] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuthStore();

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await profileApi.getMyProfile();
      setProfile({
        firstName: data.firstName,
        bio: data.bio,
        department: data.department,
        year: data.year,
        interests: data.interests,
      });
    } catch (error: any) {
      console.log('No profile yet');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage('');

    try {
      if (user?.isProfileComplete) {
        await profileApi.updateProfile(profile);
        setMessage('Profile updated successfully!');
      } else {
        await profileApi.createProfile(profile);
        updateUser({ ...user!, isProfileComplete: true });
        setMessage('Profile created successfully!');
      }
    } catch (error: any) {
      setMessage('Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddInterest = () => {
    if (newInterest.trim() && !profile.interests.includes(newInterest.trim())) {
      setProfile({
        ...profile,
        interests: [...profile.interests, newInterest.trim()],
      });
      setNewInterest('');
    }
  };

  const handleRemoveInterest = (interest: string) => {
    setProfile({
      ...profile,
      interests: profile.interests.filter((i) => i !== interest),
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="profile-container">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div className="profile-header">
          <h1>Your Profile</h1>
          <button className="btn btn-secondary" onClick={handleLogout}>
            Logout
          </button>
        </div>

        <form onSubmit={handleSave} className="profile-form">
          {message && (
            <div className={`message ${message.includes('success') ? 'success-message' : 'error-message'}`}>
              {message}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              id="firstName"
              type="text"
              value={profile.firstName}
              onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="department">Department</label>
            <input
              id="department"
              type="text"
              value={profile.department}
              onChange={(e) => setProfile({ ...profile, department: e.target.value })}
              placeholder="e.g., Computer Science"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="year">Year</label>
            <select
              id="year"
              value={profile.year}
              onChange={(e) => setProfile({ ...profile, year: parseInt(e.target.value) })}
              required
            >
              <option value={1}>1st Year</option>
              <option value={2}>2nd Year</option>
              <option value={3}>3rd Year</option>
              <option value={4}>4th Year</option>
              <option value={5}>5th Year+</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="bio">Bio</label>
            <textarea
              id="bio"
              value={profile.bio}
              onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
              placeholder="Tell us about yourself..."
              required
            />
          </div>

          <div className="form-group">
            <label>Interests</label>
            <div className="interests-input-container">
              <input
                type="text"
                value={newInterest}
                onChange={(e) => setNewInterest(e.target.value)}
                placeholder="Add an interest"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddInterest();
                  }
                }}
              />
              <button type="button" onClick={handleAddInterest}>
                Add
              </button>
            </div>
            {profile.interests.length > 0 && (
              <div className="interests-tags">
                {profile.interests.map((interest) => (
                  <div key={interest} className="interest-tag-removable">
                    <span>{interest}</span>
                    <button type="button" onClick={() => handleRemoveInterest(interest)}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Profile'}
          </button>
        </form>

        <div className="profile-actions">
          <button className="btn btn-secondary btn-block" onClick={() => navigate('/')}>
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};
